import { Button } from "@pepperstone/pepper-ui-kit";
import type React from "react";
import { useTranslation } from "react-i18next";
import { BrandedButton } from "@/components/general/branded-button/BrandedButton";
import style from "./NavButtons.module.css";

type ButtonVariant = React.ComponentProps<typeof Button>["variant"];

type NavButtonsProps = {
  className?: string;
  nextLabel?: string;
  backLabel?: string;
  nextDisabled?: boolean;
  backDisabled?: boolean;
  nextIsLoading?: boolean;
  backIsLoading?: boolean;
  nextAction?: () => void;
  backAction?: () => void;
  nextBrand?: string;
  nextVariant?: ButtonVariant;
  backVariant?: ButtonVariant;
  /**
   * Full custom component for the next slot. Rendered instead of the default next button
   * or BrandedButton (e.g. ApplePayButton, GooglePayButton). Independent of nextBrand.
   */
  nextButton?: React.ReactNode;
};

export const NavButtons = ({
  className,
  nextLabel,
  backLabel,
  nextAction,
  backAction,
  nextDisabled,
  backDisabled,
  nextIsLoading,
  backIsLoading,
  nextBrand,
  nextVariant,
  backVariant,
  nextButton,
}: NavButtonsProps) => {
  const { t } = useTranslation();

  return (
    <div className={`${style.container} ${className}`}>
      {nextButton ? (
        <div className={style.button}>{nextButton}</div>
      ) : nextBrand ? (
        <BrandedButton
          data-testid="nav-buttons-next"
          disabled={nextDisabled}
          brand={nextBrand}
          next={() => {
            nextAction?.();
          }}
        >
          {nextLabel || t("global.nav_buttons.continue")}
        </BrandedButton>
      ) : (
        <Button
          data-testid="nav-buttons-next"
          type="button"
          variant={nextVariant || "ps-btn-primary"}
          onClick={() => {
            nextAction?.();
          }}
          isLoading={nextIsLoading}
          disabled={nextDisabled}
          size="ps-btn-lg"
          className={style.button}
        >
          {nextLabel || t("global.nav_buttons.continue")}
        </Button>
      )}
      <Button
        data-testid="nav-buttons-back"
        type="button"
        variant={backVariant || "ps-btn-outline"}
        onClick={() => {
          backAction?.();
        }}
        disabled={backDisabled}
        isLoading={backIsLoading}
        size="ps-btn-lg"
        className={style.button}
      >
        {backLabel || t("global.nav_buttons.back")}
      </Button>
    </div>
  );
};
